// ==================== GURUKUL — CONSENT FLOW (shared) ====================
// ONE component used by the enrollment wizard AND the "Review & sign" re-consent, so they can never drift apart.
// Faithful to the old eGurukul: ONE STEP PER POLICY — open the policy, only then the agree box unlocks, and the
// person ticks "I have read <policy> and I agree to its terms" for EACH policy. New rules (Rishi, 2026-09-21):
//  • read ONCE per enrollment (one consent covers every learner enrolled together);
//  • a policy unchanged since the person last agreed says so (date + record number), reading is OPTIONAL, they still tick;
//  • a changed / never-agreed policy must be opened (the server records the opening and refuses the signature otherwise).
import { showToast, showConfirm } from '../components/toasts.js';

/** "v1 (imported from the eGurukul app)" → "v1": people signing see the version, not the record-keeping note. */
export const shortLabel = l => String(l ?? '').replace(/s*(.*)s*$/, '');
const esc = t => String(t ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
async function api(path, options = {}) {
  const res = await fetch(path, { credentials: 'include', headers: { 'Content-Type': 'application/json' }, ...options });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || 'Request failed');
  return data;
}

const CSS = `
.gk-ci{display:flex;align-items:flex-start;gap:11px;padding:12px 14px;border:1.5px solid var(--border,#d9dfdb);border-radius:6px;margin:0 0 9px;cursor:pointer;background:var(--card-bg,#fff);font-weight:normal;line-height:1.55;font-size:14px;color:var(--text,#222);transition:border-color .15s,background .15s;}
.gk-ci:hover{border-color:#2D6A4F;}
.gk-ci.chk{border-color:#2D6A4F;background:rgba(45,106,79,.09);}
.gk-ci.locked{opacity:.5;pointer-events:none;}
.gk-ci input[type=checkbox]{width:17px;height:17px;flex-shrink:0;margin-top:3px;accent-color:#2D6A4F;cursor:pointer;}
.gk-ci span{flex:1;overflow-wrap:anywhere;white-space:normal;}
.gk-cf-h{font-size:1.05rem;font-weight:700;margin:22px 0 10px;padding-bottom:6px;border-bottom:1px solid var(--border,#d9dfdb);}
.gk-cf-h:first-child{margin-top:4px;}
.gk-cf-info{border-left:3px solid #D4A017;background:rgba(212,160,23,.12);border-radius:0 6px 6px 0;padding:13px 16px;margin:14px 0;font-size:14px;line-height:1.65;}
.gk-cf-warn{border-left-color:#ea580c;background:rgba(234,88,12,.10);}
.gk-cf-ok{border-left-color:#2D6A4F;background:rgba(45,106,79,.09);}
.gk-cf-sum{width:100%;border-collapse:collapse;}
.gk-cf-sum th,.gk-cf-sum td{padding:11px 12px;border-bottom:1px solid var(--border,#d9dfdb);text-align:left;vertical-align:top;white-space:normal;overflow-wrap:anywhere;line-height:1.55;font-size:14px;}
.gk-cf-sum th{width:32%;font-weight:700;}
.gk-cf-sum td.yn{width:64px;font-weight:700;text-align:center;}
@media (max-width:640px){.gk-cf-sum th{width:38%;}}
`;
export function ensureStyle() {
  if (document.getElementById('gkCfStyle')) return;
  const st = document.createElement('style');
  st.id = 'gkCfStyle';
  st.textContent = CSS;
  document.head.appendChild(st);
}

/** Show one version's text in a sandboxed frame (no scripts) inside the container. */
export function showText(container, v) {
  if (v.body_html) {
    container.innerHTML = '<iframe sandbox="" style="width:100%;height:68vh;border:1px solid var(--border);border-radius:6px;background:#fff;"></iframe>';
    container.querySelector('iframe').srcdoc = v.body_html;
  } else {
    container.innerHTML = (v.checklist?.notice ? `<p class="page-intro">${esc(v.checklist.notice)}</p>` : '') +
      (v.checklist?.sections || []).map(s => `<h4>${esc(s.heading)}</h4><ul>${s.items.map(i => `<li>${esc(i.label)}</li>`).join('')}</ul>`).join('');
  }
}

/**
 * @param {HTMLElement} box
 * @param {object} o
 * @param {string} o.heading                       shown above the steps
 * @param {object} [o.firstStep]                   { label, render(w) → html, bind(w), validate(w) → error|null }
 * @param {Array}  [o.learnerChoice]               [{id, learner_id}] — shown on the review step (re-consent)
 * @param {function} [o.reviewRows]                (w) → extra <tr> rows for the review table
 * @param {string} o.submitLabel
 * @param {function} o.confirmText                 (w) → question shown before signing
 * @param {function} o.submit                      ({acceptances, signature, learnerIds, w}) → Promise<result>
 * @param {function} o.onSuccess                   (result, box) → void
 * @param {function} [o.onCancel]
 * @param {string}   [o.context]                   'enrollment' (default) or 'volunteer' — which documents to sign
 * @param {function} [o.conditions]                (w) → {teaches:true,…}: checklist items with a matching `condition` show only when true
 */
export async function runConsentFlow(box, o) {
  ensureStyle();
  box.innerHTML = '<p class="empty-state">Loading…</p>';
  let docs, status;
  try {
    const ctx = encodeURIComponent(o.context || 'enrollment');
    docs = (await api('/api/gurukul/legal?required=1&context=' + ctx)).documents;
    status = (await api('/api/gurukul/legal?consent_status=1&context=' + ctx)).policies;
  } catch (err) { box.innerHTML = `<p class="empty-state">${esc(err.message)}</p>`; return; }
  const policies = status;                                   // policies with their unchanged/changed/new status
  const acks = docs.filter(d => d.kind === 'acknowledgment');
  const opts = docs.filter(d => d.kind === 'optional');
  if (!policies.length) { box.innerHTML = '<p class="empty-state">There are no policies to sign right now.</p>'; return; }

  const steps = [
    ...(o.firstStep ? [{ type: 'first', label: o.firstStep.label }] : []),
    ...policies.map(p => ({ type: 'policy', label: p.title, p })),
    { type: 'ack', label: 'Acknowledgments' },
    { type: 'opt', label: 'Optional consents & signature' },
    { type: 'review', label: 'Review & sign' },
  ];
  // Items with a `condition` (e.g. "teaches") only apply when the first step said so.
  const cond = () => (o.conditions ? o.conditions(w) : {});
  const secsOf = a => a.checklist.sections.map(sec => ({ ...sec, items: sec.items.filter(i => !i.condition || cond()[i.condition]) })).filter(sec => sec.items.length);
  const ackKeys = () => acks.flatMap(a => secsOf(a).flatMap(sec => sec.items.map(i => `${a.version_id}:${i.id}`)));
  const ackDone = () => ackKeys().every(k => w.ticked.has(k));
  const w = { i: 0, opened: new Set(), agreed: new Set(), ticked: new Set(), optional: {}, signature: '', chosen: new Set((o.learnerChoice || []).map(l => l.id)), busy: false };

  const header = () => `
    <h2 style="font-size:1.1rem;margin:0 0 6px;">${esc(o.heading)} — Step ${w.i + 1} of ${steps.length} — ${esc(steps[w.i].label)}</h2>
    <div style="height:4px;background:#e5e9e6;border-radius:2px;margin-bottom:10px;"><div style="height:4px;border-radius:2px;background:#2D6A4F;width:${Math.round(((w.i + 1) / steps.length) * 100)}%;"></div></div>`;
  const nav = (ok, label = 'Next') => `
    <div class="btn-group" style="display:flex;gap:8px;margin-top:16px;">
      ${w.i > 0 ? '<button type="button" class="secondary" id="gkCfBack">Back</button>' : (o.onCancel ? '<button type="button" class="secondary" id="gkCfCancel">Cancel</button>' : '')}
      <button type="button" id="gkCfNext" ${ok ? '' : 'disabled'}>${esc(label)}</button>
    </div>`;
  const sigOk = () => w.signature.trim().length >= 5 && /\s/.test(w.signature.trim());
  const fmtDate = d => new Date(d).toLocaleDateString();

  function render() {
    const s = steps[w.i];
    if (s.type === 'first') {
      box.innerHTML = header() + o.firstStep.render(w) + nav(true);
      o.firstStep.bind && o.firstStep.bind(w);
    } else if (s.type === 'policy') {
      const p = s.p;
      const unchanged = p.status === 'unchanged';
      const readBtn = `<button type="button" class="primary" id="gkCfRead" style="padding:4px 12px;margin:0 4px;">click here</button>`;
      const notice = unchanged
        ? `<div class="gk-cf-info gk-cf-ok" style="margin:0 0 8px;">Unchanged since you agreed on ${esc(fmtDate(p.last_agreed.at))} (${esc(p.last_agreed.event_no)}). To read it again (optional), ${readBtn}.</div>`
        : p.status === 'changed'
          ? `<div class="gk-cf-info gk-cf-warn" style="margin:0 0 8px;">This policy has <strong>changed</strong> since you agreed to ${esc(shortLabel(p.last_agreed.version_label))} on ${esc(fmtDate(p.last_agreed.at))}. Please ${readBtn} to read it before agreeing.</div>`
          : `<p style="margin:0 0 8px;">Please ${readBtn} to read the policy in full before agreeing.</p>`;
      const canAgree = unchanged || w.opened.has(p.version_id);
      box.innerHTML = header() + `
        ${notice}
        <div id="gkCfText"></div>
        <label class="gk-ci${w.agreed.has(p.version_id) ? ' chk' : ''}${canAgree ? '' : ' locked'}" id="gkCfAgreeRow" style="margin-top:14px;"><input type="checkbox" id="gkCfAgree" ${canAgree ? '' : 'disabled'} ${w.agreed.has(p.version_id) ? 'checked' : ''}>
          <span>${unchanged ? `I confirm the ${esc(p.title)} has not changed and I still agree to its terms.` : `I have read the ${esc(p.title)} and I agree to its terms.`}</span></label>
        ${!canAgree ? '<p class="page-intro" id="gkCfLockNote">The box unlocks after you open the policy.</p>' : ''}` + nav(w.agreed.has(p.version_id));
      document.getElementById('gkCfRead').addEventListener('click', async () => {
        try {
          showText(document.getElementById('gkCfText'), await api('/api/gurukul/legal?version=' + p.version_id));   // the server records the opening
          w.opened.add(p.version_id);
          const cb = document.getElementById('gkCfAgree');
          cb.disabled = false; cb.parentElement.classList.remove('locked');
          document.getElementById('gkCfLockNote')?.remove();
        } catch (e) { showToast(e.message, 'error'); }
      });
      document.getElementById('gkCfAgree').addEventListener('change', e => {
        e.target.checked ? w.agreed.add(p.version_id) : w.agreed.delete(p.version_id);
        document.getElementById('gkCfAgreeRow').classList.toggle('chk', e.target.checked);
        document.getElementById('gkCfNext').disabled = !w.agreed.has(p.version_id);
      });
    } else if (s.type === 'ack') {
      box.innerHTML = header() + `
        <div class="gk-cf-info">By ticking each box below you confirm your agreement. Every box must be ticked individually — these are safety and liability commitments. Your IP address, date and time are recorded for audit.</div>
        ${acks.map(a => secsOf(a).map(sec => `<h4 class="gk-cf-h">${esc(sec.heading)}</h4>${sec.items.map(i => `
          <label class="gk-ci${w.ticked.has(a.version_id + ':' + i.id) ? ' chk' : ''}"><input type="checkbox" class="gk-cf-ack" data-k="${a.version_id}:${esc(i.id)}" ${w.ticked.has(a.version_id + ':' + i.id) ? 'checked' : ''}> <span>${esc(i.label)}</span></label>`).join('')}`).join('')).join('')}
        <p id="gkCfAckCount" class="page-intro"></p>` + nav(ackDone());
      const upd = () => { const keys = ackKeys(); document.getElementById('gkCfAckCount').textContent = `${keys.filter(k => w.ticked.has(k)).length} of ${keys.length} ticked.`; document.getElementById('gkCfNext').disabled = !ackDone(); };
      upd();
      box.querySelectorAll('.gk-cf-ack').forEach(cb => cb.addEventListener('change', () => { cb.checked ? w.ticked.add(cb.dataset.k) : w.ticked.delete(cb.dataset.k); cb.parentElement.classList.toggle('chk', cb.checked); upd(); }));
    } else if (s.type === 'opt') {
      box.innerHTML = header() + `
        ${opts.map(x => `<h4 class="gk-cf-h">${esc(x.title)}</h4>${x.checklist.notice ? `<div class="gk-cf-info">${esc(x.checklist.notice)}</div>` : ''}${x.checklist.sections.flatMap(sec => sec.items).map(i => `
          <label class="gk-ci${w.optional[x.version_id + ':' + i.id] ? ' chk' : ''}"><input type="checkbox" class="gk-cf-opt" data-v="${x.version_id}" data-id="${esc(i.id)}" ${w.optional[x.version_id + ':' + i.id] ? 'checked' : ''}> <span>${esc(i.label)}</span></label>`).join('')}`).join('')}
        <h4 class="gk-cf-h">Electronic signature</h4>
        <div class="form-group" style="margin-top:8px;max-width:440px;"><label for="gkCfSig">Electronic signature — type your full legal name</label><input type="text" id="gkCfSig" maxlength="120" autocomplete="off" value="${esc(w.signature)}"></div>` + nav(sigOk());
      const sig = document.getElementById('gkCfSig');
      sig.addEventListener('input', () => { w.signature = sig.value; document.getElementById('gkCfNext').disabled = !sigOk(); });
      box.querySelectorAll('.gk-cf-opt').forEach(cb => cb.addEventListener('change', () => { w.optional[cb.dataset.v + ':' + cb.dataset.id] = cb.checked; cb.parentElement.classList.toggle('chk', cb.checked); }));
    } else {
      box.innerHTML = header() + `
        <table class="gk-cf-sum"><tbody>
          ${o.reviewRows ? o.reviewRows(w) : ''}
          <tr><th>Policies</th><td>${policies.map(p => `${esc(p.title)} (${esc(shortLabel(p.version_label))}) — ${p.status === 'unchanged' && !w.opened.has(p.version_id) ? 'unchanged, re-confirmed' : 'read and agreed'}`).join('<br>')}</td></tr>
          <tr><th>Acknowledgments</th><td>All ${ackKeys().length} ticked</td></tr>
          ${opts.map(x => x.checklist.sections.flatMap(sec => sec.items).map(i => `<tr><th>Optional consent</th><td>${esc(i.label)}</td><td class="yn">${w.optional[x.version_id + ':' + i.id] ? 'Yes' : 'No'}</td></tr>`).join('')).join('')}
          <tr><th>Signed as</th><td colspan="2"><strong>${esc(w.signature.trim())}</strong></td></tr>
        </tbody></table>
        ${o.learnerChoice ? `<h4 class="gk-cf-h">Learners this covers</h4>${o.learnerChoice.map(l => `<label class="gk-ci${w.chosen.has(l.id) ? ' chk' : ''}"><input type="checkbox" class="gk-cf-learner" value="${l.id}" ${w.chosen.has(l.id) ? 'checked' : ''}> <span>${esc(l.learner_id)}</span></label>`).join('')}` : ''}
        <div class="gk-cf-info">Your signature is recorded permanently.</div>` + nav(true, o.submitLabel);
      box.querySelectorAll('.gk-cf-learner').forEach(cb => cb.addEventListener('change', () => { cb.checked ? w.chosen.add(cb.value) : w.chosen.delete(cb.value); cb.parentElement.classList.toggle('chk', cb.checked); }));
    }
    document.getElementById('gkCfBack')?.addEventListener('click', () => { w.i -= 1; render(); });
    document.getElementById('gkCfCancel')?.addEventListener('click', () => o.onCancel());
    document.getElementById('gkCfNext')?.addEventListener('click', onNext);
  }

  async function onNext() {
    const s = steps[w.i];
    if (s.type === 'first') { const err = o.firstStep.validate ? o.firstStep.validate(w) : null; if (err) { showToast(err, 'error'); return; } }
    if (w.i < steps.length - 1) { w.i += 1; render(); return; }
    if (o.learnerChoice && !w.chosen.size) { showToast('Choose at least one learner', 'error'); return; }
    if (w.busy) return;
    if (!(await showConfirm(o.confirmText(w)))) return;
    w.busy = true;
    document.getElementById('gkCfNext').disabled = true;
    const acceptances = [
      ...policies.map(p => ({ version_id: p.version_id, agreed: true, basis: p.status === 'unchanged' ? 'unchanged_since_previous' : 'read_now' })),
      ...acks.map(a => ({ version_id: a.version_id, items: secsOf(a).flatMap(sec => sec.items.map(i => i.id)).filter(id => w.ticked.has(a.version_id + ':' + id)) })),
      ...opts.map(x => ({ version_id: x.version_id, items: Object.fromEntries(x.checklist.sections.flatMap(sec => sec.items).map(i => [i.id, !!w.optional[x.version_id + ':' + i.id]])) })),
    ];
    try {
      const result = await o.submit({ acceptances, signature: w.signature.trim(), learnerIds: [...w.chosen], w });
      o.onSuccess(result, box);
    } catch (err) {
      w.busy = false;
      showToast(err.message, 'error');
      const n = document.getElementById('gkCfNext'); if (n) n.disabled = false;
    }
  }

  render();
}
